import { useState } from "react";
import {
  IconButton,
  Typography,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
} from "@mui/material";
import { palette } from "../../../theme";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CloseIcon from "@mui/icons-material/Close";
import API from "../../../api";
import { startToast, updateToastError, updateToastSuccess } from "../../../utils/notification-loading";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { MUTED, BORDER } from "../../dashboard/dashboard-components/resellerUi";
import { dialogPaperProps, dialogTitleSx, formatDate } from "../ticketsUi";

dayjs.extend(utc);

// Dialog dei ticket hardware: usati dalla lista unificata in ../index.jsx.

export const OK_COLOR = "#34a327";
export const WARN_COLOR = "#EF9F27";

// Fasi della procedura di verifica (fase = ultima completata, 0..6).
export const PHASES = [
  { n: 1, label: "Ethernet cable control", desc: "Verify that all Ethernet cables are working" },
  { n: 2, label: "Verification of LAN ports", desc: "Verify that all LAN ports are working" },
  { n: 3, label: "IP Assignment to LECS", desc: "Ensure that the LECS has been assigned an IP address for output to the Internet." },
  { n: 4, label: "Mirror Port Verification on LECS Enterprise", desc: "In the case of LECS Enterprise, verify that the mirror port is configured to allow outbound traffic" },
  { n: 5, label: "Firewall and log verification", desc: "Review firewall rules and logs to ensure that LECS can reach the domain `securitylecs.com` on ports 12350/12349" },
  { n: 6, label: "Moving the device", desc: "If all previous checks do not solve the problem, move the device to a flat network (without segmentation)" },
];

// ticket = riga hardware selezionata (null = dialog chiuso).
export const HardwareTicketDialog = ({ ticket, onClose }) => (
  <Dialog open={Boolean(ticket)} onClose={onClose} fullWidth maxWidth="md" slotProps={dialogPaperProps}>
    {ticket && <ToDoLiST ticket={ticket} onClose={onClose} />}
  </Dialog>
);

export const DeleteTicketDialog = ({ ticket, onCancel, onConfirm }) => (
  <Dialog open={Boolean(ticket)} onClose={onCancel} slotProps={dialogPaperProps}>
    <DialogTitle sx={dialogTitleSx}>Confirm Deletion</DialogTitle>
    <DialogContent sx={{ pt: "20px !important" }}>
      <Typography sx={{ fontSize: 14 }}>Are you sure you want to delete this ticket?</Typography>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2 }}>
      <Button onClick={onCancel} variant="outlined" size="small">
        Cancel
      </Button>
      <Button onClick={onConfirm} color="error" variant="contained" size="small">
        Delete
      </Button>
    </DialogActions>
  </Dialog>
);

// Riga di una fase: stato (fatta / sbloccata / bloccata), descrizione e data di esecuzione.
const PhaseRow = ({ phase, fase, dateFase, onDone }) => {
  const done = fase >= phase.n;
  // Una fase si sblocca solo dopo l'esecuzione della precedente (la prima è sempre libera).
  const locked = !done && phase.n > 1 && !dateFase[phase.n - 1];
  const executed = formatDate(dateFase[phase.n]);
  const StateIcon = done ? CheckCircleOutlineIcon : locked ? LockOutlinedIcon : RadioButtonUncheckedIcon;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        p: 1.5,
        border: `1px solid ${done ? "rgba(69,144,149,0.35)" : BORDER}`,
        borderRadius: "10px",
        backgroundColor: done ? "rgba(69,144,149,0.08)" : "transparent",
      }}
    >
      <StateIcon sx={{ flexShrink: 0, color: done ? palette.primary.light : MUTED }} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: locked ? MUTED : undefined }}>
          {phase.n}. {phase.label}
        </Typography>
        <Typography sx={{ fontSize: 12, color: MUTED, wordBreak: "break-word" }}>{phase.desc}</Typography>
        {dateFase[phase.n] && (
          <Typography sx={{ fontSize: 12, color: palette.primary.light, mt: 0.25 }}>
            Executed on {executed}
          </Typography>
        )}
      </Box>
      {!done && (
        <Button variant="outlined" size="small" disabled={locked} onClick={() => onDone(phase.n)} sx={{ flexShrink: 0 }}>
          Mark as done
        </Button>
      )}
    </Box>
  );
};

const ToDoLiST = ({ ticket, onClose }) => {
  const [fase, setFase] = useState(ticket.fase);
  const [dateFase, setDateFase] = useState(ticket.phasesDate || {});
  const [requestNewLecs] = useState(ticket.requestNewLecs);

  const TOChangePhaseofTicket = (phase) => {
    const d = dayjs().utc().format();
    const updatedDateFase = { ...dateFase, [phase]: d };
    setFase(phase);
    setDateFase({ ...dateFase, [phase]: d });

    API.changeHardwareTicketPhases({
      idTicket: ticket._id,
      phase: phase,
      dateFase: updatedDateFase,
    }).then((res) => {});
  };

  function handleChangeHardware(matricola, idTicket) {
    startToast();
    API.askChangeLecs({ serial_number: matricola, id: idTicket })
      .then((res) => {
        if (res.status === 200) {
          updateToastSuccess("Successfull");
        }
      })
      .catch(() => {
        updateToastError("Error on handling the ticket");
      });

    onClose();
  }

  return (
    <>
      <DialogTitle sx={dialogTitleSx}>
        <Box component="span">
          <Box component="span" sx={{ color: MUTED, fontWeight: 500 }}>
            Hardware ticket{" "}
          </Box>
          {ticket.ragioneSociale}
        </Box>
        <IconButton size="small" onClick={() => onClose()} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: "20px !important" }}>
        <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <Box>
            <Typography sx={{ fontSize: 12, color: MUTED }}>LECS S.N</Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{ticket.matricola || "-"}</Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 12, color: MUTED }}>Start date</Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{formatDate(ticket.dataCreazione)}</Typography>
          </Box>
        </Stack>

        <Stack spacing={1.25}>
          {PHASES.map((phase) => (
            <PhaseRow key={phase.n} phase={phase} fase={fase} dateFase={dateFase} onDone={TOChangePhaseofTicket} />
          ))}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          variant="contained"
          size="small"
          disabled={!(fase === 6 && !requestNewLecs)}
          onClick={() => {
            handleChangeHardware(ticket.matricola, ticket._id);
          }}
        >
          Request LECS replacement
        </Button>
      </DialogActions>
    </>
  );
};
